'use client';

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { useControl } from 'react-map-gl/mapbox';
import type { ControlPosition, IControl } from 'react-map-gl/mapbox';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import getHelpCenterUrl from '@alltrails/core/utils/getHelpCenterUrl';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import MapLanguageDropdown from './MapLanguageDropdown';
import './MapAttribution.scss';

function createCustomAttributionControl(): IControl & { setCompact: (isCompact: boolean) => void; getContainer: () => HTMLDivElement | undefined } {
  let container: HTMLDivElement | undefined;
  let innerContainer: HTMLDivElement | undefined;
  let toggleButton: HTMLButtonElement | undefined;

  return {
    onAdd() {
      container = document.createElement('div');
      container.className = 'mapboxgl-ctrl mapboxgl-ctrl-attrib';
      container.style.backgroundColor = 'hsla(0, 0%, 100%, .75)';

      toggleButton = document.createElement('button');
      toggleButton.className = 'mapboxgl-ctrl-attrib-button';
      toggleButton.type = 'button';
      toggleButton.style.display = 'none';

      const iconSpan = document.createElement('span');
      iconSpan.className = 'mapboxgl-ctrl-icon';
      iconSpan.setAttribute('aria-hidden', 'true');
      iconSpan.setAttribute('title', 'Toggle attribution');
      toggleButton.appendChild(iconSpan);

      toggleButton.addEventListener('click', () => {
        container?.classList.toggle('mapboxgl-compact-show');
      });

      innerContainer = document.createElement('div');
      innerContainer.className = 'mapboxgl-ctrl-attrib-inner';

      container.appendChild(toggleButton);
      container.appendChild(innerContainer);
      return container;
    },

    onRemove() {
      container?.remove();
      container = undefined;
      innerContainer = undefined;
      toggleButton = undefined;
    },

    getDefaultPosition(): ControlPosition {
      return 'bottom-right';
    },

    getContainer() {
      return innerContainer;
    },

    setCompact(isCompact: boolean) {
      if (container && toggleButton) {
        if (isCompact) {
          container.classList.add('mapboxgl-compact');
          toggleButton.style.display = 'block';
        } else {
          container.classList.remove('mapboxgl-compact');
          container.classList.remove('mapboxgl-compact-show');
          toggleButton.style.display = 'none';
        }
      }
    }
  };
}

type Props = {
  // Force the collapsed (info-button toggle) presentation at all screen sizes, e.g. for embeds. Defaults to mobile-only.
  compact?: boolean;
};

const MapAttribution = ({ compact }: Props) => {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const isMobile = useIsMobileSizedScreen();

  const ctrl = useControl(() => createCustomAttributionControl(), {
    position: 'bottom-right'
  });

  const [container, setContainer] = useState<HTMLDivElement | undefined>();

  useEffect(() => {
    const containerElement = ctrl.getContainer();
    if (containerElement && containerElement !== container) {
      setContainer(containerElement);
    }
  }, [ctrl, container]);

  useEffect(() => {
    ctrl.setCompact(compact ?? isMobile);
  }, [ctrl, isMobile, compact]);

  const content = useMemo(
    () => (
      <>
        <span>
          ©{' '}
          <a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noopener noreferrer">
            Mapbox
          </a>{' '}
          ©{' '}
          <a href="http://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">
            OpenStreetMap
          </a>
        </span>
        {' | '}
        <a href={getHelpCenterUrl(languageRegionCode, 11555324555924)} target="_blank" rel="noopener noreferrer">
          {intl.formatMessage({ defaultMessage: 'Map legend' })}
        </a>
        {' | '}
        <a href={getHelpCenterUrl(languageRegionCode, 50193218305556)} target="_blank" rel="noopener noreferrer">
          {intl.formatMessage({ defaultMessage: 'Data attribution' })}
        </a>
        {' | '}
        <a href="https://www.mapbox.com/map-feedback/" target="_blank" rel="noopener noreferrer">
          {intl.formatMessage({ defaultMessage: 'Improve this map' })}
        </a>
        {' | '}
        <a href={wrapUrlSafe('/privacy', languageRegionCode)} rel="nofollow">
          {intl.formatMessage({ defaultMessage: 'Privacy Policy' })}
        </a>
        {' | '}
        <a href={wrapUrlSafe('/terms', languageRegionCode)} rel="nofollow">
          {intl.formatMessage({ defaultMessage: 'Terms' })}
        </a>
        {' | '}
        <MapLanguageDropdown languageRegionCode={languageRegionCode} pageReloadOnLanguageChange />
      </>
    ),
    [intl, languageRegionCode]
  );

  return container ? createPortal(content, container) : null;
};

export default MapAttribution;
